import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { UnitType } from "~/types/company";

// LocalStorage key for persisting selected company
const SELECTED_COMPANY_ID = "selected_company_id";

interface CalendarEvent {
  externalId: string;
  kind: string;
}

interface EventMembership {
  bufferedEndTime: string;
  bufferedStartTime: string;
  user: string;
  userEmail: string;
  userName: string;
}

interface InviteesCounter {
  active: number;
  limit: number;
  total: number;
}

interface MeetLocation {
  location: string;
  type: string;
}

export interface MeetData {
  calendarEvent: CalendarEvent;
  createdAt: string;
  endTime: string;
  eventGuests: [];
  eventMemberships: EventMembership[];
  eventType: string;
  inviteesCounter: InviteesCounter;
  location: MeetLocation;
  meetingNotesHtml: string | null;
  meetingNotesPlain: string | null;
  name: string;
  startTime: string;
  status: string;
  updatedAt: string;
  uri: string;
}

export interface CompanyAddress {
  isMain: boolean | null;
  countriesId: string | null;
  country: string;
  zipCode: string | null;
  state: string | null;
  city: string | null;
  municipality: string | null;
  suburb: string | null;
  street: string | null;
  externalNumber: string | null;
  internalNumber: string | null;
  isActive: boolean;
  id: string;
  companyId: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface IndustryData {
  name: string;
  isActive: boolean;
  id: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}
export interface CompanyData {
  website: string | null;
  description: string | null;
  logoPath: string | null;
  commercialName: string | null;
  linkedinUrl: string | null;
  employeesLength: string | null;
  annualIncome: string | null;
  status: string | null;
  companySize: string | null;
  fiscalIdentifier: string;
  identifierType: string;
  legalName: string;
  invoicesEmail: string | null;
  questionInfo: string | null;
  hasBusinessUnits: boolean | null;
  isActive: boolean;
  accountingType: string | null;
  closingDays: string | null;
  comparable: string | null;
  accountingSystem: string | null;
  accountingSystemOther: string | null;
  initialDataUpload: boolean;
  id: string;
  originCity: string | null;
  primaryCurrency: string | null;
  industry: IndustryData | null;
  industryId: string | null;
  subIndustryId: string | null;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  addresses: CompanyAddress[];
}

export interface Company {
  id: string;
  legalName: string;
  commercialName: string;
  fiscalIdentifier: string;
  lastPeriodRegistered: string | null;
  defaultTrimestralPeriod: string | null;
  defaultAnualPeriod: string | null;
  primaryCurrency: string | null;
  status: string | null;
  preferredUnitType: UnitType | null;
}

export type MeetStatus = "created" | "pending" | "finished";

/**
 * Calculate default trimestral and anual periods from lastPeriodRegistered (Mensual format)
 * @param lastPeriodRegistered - Period in format "MMM-YYYY" (e.g., "Feb-2026", "Jan-2025", "Dec-2024")
 * @returns Object with defaultTrimestralPeriod and defaultAnualPeriod, or nulls if invalid
 */
function calculateDefaultPeriods(lastPeriodRegistered: string | null): {
  defaultTrimestralPeriod: string | null;
  defaultAnualPeriod: string | null;
} {
  if (!lastPeriodRegistered) {
    return {
      defaultTrimestralPeriod: null,
      defaultAnualPeriod: null,
    };
  }

  // Parse mensual format: "MMM-YYYY" (e.g., "Feb-2026", "Jan-2025")
  const match = lastPeriodRegistered.match(/^([A-Za-z]{3})-(\d{4})$/);
  if (!match) {
    return {
      defaultTrimestralPeriod: null,
      defaultAnualPeriod: null,
    };
  }

  const [, monthStr, yearStr] = match;
  const yearShort = yearStr.slice(-2);

  // Map English month abbreviation to month number (0-11)
  const monthMap: { [key: string]: number } = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };

  const monthIndex = monthMap[monthStr];
  if (monthIndex === undefined) {
    return {
      defaultTrimestralPeriod: null,
      defaultAnualPeriod: null,
    };
  }

  // Calculate quarter (1-4) from month
  const quarter = Math.floor(monthIndex / 3) + 1;
  const defaultTrimestralPeriod = `Q${quarter}-${yearShort}`;

  // Anual period is the same year as lastPeriodRegistered
  const defaultAnualPeriod = lastPeriodRegistered;

  return {
    defaultTrimestralPeriod,
    defaultAnualPeriod,
  };
}

// The main User object interface
export interface UserData {
  id: string;
  email: string;
  firstName: string;
  secondName: string | null;
  surname: string;
  secondSurname: string | null;
  phone: string | null;
  secondPhone: string | null;
  jobPosition: string | null;
  otherJobPosition: string | null;
  showFirstSteps: boolean | null;
  onboardingStep: number | null;
  externalId: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  meetData: MeetData;
  meetStatus: MeetStatus;
  companies: Company[] | [];
}

// The main Redux state structure
export interface AuthState {
  user: UserData | null;
  isAuthenticated: boolean;
  isLoadingAuth: boolean;
  isSigningIn: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoadingAuth: true,
  isSigningIn: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Action to set the full user profile data upon login/fetch
    setUserData: (state, action: PayloadAction<UserData>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.isLoadingAuth = false;
      state.isSigningIn = false;

      // Enrich companies with calculated default periods
      if (state.user?.companies && state.user.companies.length > 0) {
        state.user.companies = state.user.companies.map((company) => {
          const { defaultTrimestralPeriod, defaultAnualPeriod } =
            calculateDefaultPeriods(company.lastPeriodRegistered);
          return {
            ...company,
            defaultTrimestralPeriod,
            defaultAnualPeriod,
          };
        });
      }

      // Restore previously selected company from localStorage on login
      if (state.user?.companies && state.user.companies.length > 0) {
        const savedCompanyId = localStorage.getItem(SELECTED_COMPANY_ID);
        if (savedCompanyId) {
          const savedIndex = state.user.companies.findIndex(
            (c) => c.id === savedCompanyId,
          );
          // If saved company exists and is not already first, move it to front
          if (savedIndex > 0) {
            const savedCompany = state.user.companies[savedIndex];
            state.user.companies = [
              savedCompany,
              ...state.user.companies.slice(0, savedIndex),
              ...state.user.companies.slice(savedIndex + 1),
            ];
          }
        }
      }
    },

    // Action to update only the meet status (for efficient updates)
    updateMeetStatus: (state, action: PayloadAction<MeetStatus>) => {
      if (state.user) {
        state.user.meetStatus = action.payload;
      }
    },

    // Handles sign-out
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoadingAuth = false;
      state.isSigningIn = false;
      // Clear selected company from localStorage on logout
      localStorage.removeItem(SELECTED_COMPANY_ID);
    },

    // Handles the initial auth loading state
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoadingAuth = action.payload;
    },

    // Set sign-in flow flag
    setSigningIn: (state, action: PayloadAction<boolean>) => {
      state.isSigningIn = action.payload;
    },

    // Switch primary company by moving selected company to first position
    setPrimaryCompany: (state, action: PayloadAction<string>) => {
      if (!state.user || !state.user.companies) {
        return;
      }

      const companyId = action.payload;
      const companies = state.user.companies;
      const selectedIndex = companies.findIndex((c) => c.id === companyId);

      if (selectedIndex > 0) {
        // Move the selected company to the front
        const selectedCompany = companies[selectedIndex];
        state.user.companies = [
          selectedCompany,
          ...companies.slice(0, selectedIndex),
          ...companies.slice(selectedIndex + 1),
        ];
      }

      // Persist selected company to localStorage
      localStorage.setItem(SELECTED_COMPANY_ID, companyId);
    },
  },
});

export const {
  setUserData,
  updateMeetStatus,
  logout,
  setAuthLoading,
  setSigningIn,
  setPrimaryCompany,
} = authSlice.actions;
export default authSlice.reducer;
